import { useMutation, useQueryClient } from 'react-query';

import type { IStandardErrorResponse } from 'src/api/common';
import type { ILead } from 'src/services/api/leads/types';
import type { IUpdateLeadStageResponse } from 'src/services/api/leads/use-update-lead-stage';
import { useNotifications } from 'src/notifications';
import { getApiErrorMessage } from 'src/services/api/utils';
import { apiClient } from 'src/services/api/client';
import { GET_LEADS_PATH } from 'src/services/api/leads/constants';
import { queryKeys } from 'src/services/api/constants';

type IUpdateLeadStagesRequest = Array<Pick<ILead, 'uuid' | 'stage'>>;

export const useUpdateLeadStages = () => {
    const queryClient = useQueryClient();
    const { addNotification } = useNotifications();

    return useMutation<
        ILead[],
        IStandardErrorResponse,
        IUpdateLeadStagesRequest
    >(async (payload) => {
        const res = await Promise.allSettled(payload.map((lead) => {
            const path = `${GET_LEADS_PATH}/${lead.uuid}/stages`;
            return apiClient(path, {
                method: 'put',
                json: lead,
            }).json<IUpdateLeadStageResponse>();
        }));

        const oldLeads = queryClient.getQueryData<ILead[]>(queryKeys.leads) || [];

        const updatedLeads = oldLeads.map((lead) => {
            const updatedLead = res
                .find((o) => o.status === 'fulfilled' && o.value.lead.uuid === lead.uuid);

            if (!updatedLead || updatedLead.status === 'rejected') {
                return lead;
            }

            return updatedLead.value.lead;
        });

        queryClient.setQueryData<ILead[]>(queryKeys.leads, updatedLeads);
        for (const lead of updatedLeads) {
            queryClient.setQueryData<ILead>([queryKeys.leads, lead.uuid], lead);
            queryClient.invalidateQueries([queryKeys.leadStageHistory, lead.uuid]);
        }

        const errors = res.filter((r) => r.status === 'rejected');
        if (errors.length > 0) {
            throw errors.map(o => o.reason);
        } else {
            addNotification('Leads updated', 'success');
        }

        return updatedLeads;
    }, {
        onError: (errors) => {
            const errorMessage = getApiErrorMessage(errors) || 'Failed to update leads';
            addNotification(errorMessage, 'error');
        }
    });
};
